"use client";

import { Badge } from "@/components/ui/badge";
import {
  Command,
  CommandItem,
  CommandEmpty,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { Command as CommandPrimitive } from "cmdk";
import { X as RemoveIcon, Check, Search } from "lucide-react";
import React, {
  KeyboardEvent,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useState,
} from "react";

interface MultiSelectorProps
  extends React.ComponentPropsWithoutRef<typeof CommandPrimitive> {
  values: Array<{ id: string; name: string; image?: string }>;
  onValuesChange: (
    value: Array<{ id: string; name: string; image?: string }>
  ) => void;
  loop?: boolean;
  keepInputOnSelect?: boolean;
  tableView?: boolean;
  onItemClick?: (item: { id: string; name: string; image?: string }) => void;
}

type MultiSelectorItemValue = {
  id: string;
  name: string;
  image?: string;
};

interface MultiSelectContextProps {
  value: Array<{ id: string; name: string; image?: string }>;
  onValueChange: (value: { id: string; name: string; image?: string }) => void;
  open: boolean;
  setOpen: (value: boolean) => void;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  activeIndex: number;
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
  ref: React.RefObject<HTMLInputElement>;
  handleSelect: (e: React.SyntheticEvent<HTMLInputElement>) => void;
  keepInputOnSelect: boolean;
  tableView: boolean;
  onItemClick?: (item: { id: string; name: string; image?: string }) => void;
}

const MultiSelectContext = createContext<MultiSelectContextProps | null>(null);

const useMultiSelect = () => {
  const context = useContext(MultiSelectContext);
  if (!context) {
    throw new Error("useMultiSelect must be used within MultiSelectProvider");
  }
  return context;
};

const MultiSelector = ({
  values: value,
  onValuesChange: onValueChange,
  loop = false,
  keepInputOnSelect = false,
  tableView = false,
  onItemClick,
  className,
  children,
  dir,
  ...props
}: MultiSelectorProps) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isValueSelected, setIsValueSelected] = React.useState(false);
  const [selectedValue, setSelectedValue] = React.useState("");

  const onValueChangeHandler = useCallback(
    (val: { id: string; name: string; image?: string }) => {
      if (value.some((item) => item.id === val.id)) {
        onValueChange(value.filter((item) => item.id !== val.id));
      } else {
        onValueChange([...value, val]);
      }
    },
    [value, onValueChange]
  );

  const handleSelect = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      e.preventDefault();
      const target = e.currentTarget;
      const selection = target.value.substring(
        target.selectionStart ?? 0,
        target.selectionEnd ?? 0
      );

      setSelectedValue(selection);
      setIsValueSelected(selection === inputValue);
    },
    [inputValue]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      e.stopPropagation();
      const target = inputRef.current;

      if (!target) return;

      const moveNext = () => {
        const nextIndex = activeIndex + 1;
        setActiveIndex(
          nextIndex > value.length - 1 ? (loop ? 0 : -1) : nextIndex
        );
      };

      const movePrev = () => {
        const prevIndex = activeIndex - 1;
        setActiveIndex(prevIndex < 0 ? value.length - 1 : prevIndex);
      };

      const moveCurrent = () => {
        const newIndex =
          activeIndex - 1 <= 0
            ? value.length - 1 === 0
              ? -1
              : 0
            : activeIndex - 1;
        setActiveIndex(newIndex);
      };

      switch (e.key) {
        case "ArrowLeft":
          if (dir === "rtl") {
            if (value.length > 0 && (activeIndex !== -1 || loop)) {
              moveNext();
            }
          } else {
            if (value.length > 0 && target.selectionStart === 0) {
              movePrev();
            }
          }
          break;

        case "ArrowRight":
          if (dir === "rtl") {
            if (value.length > 0 && target.selectionStart === 0) {
              movePrev();
            }
          } else {
            if (value.length > 0 && (activeIndex !== -1 || loop)) {
              moveNext();
            }
          }
          break;

        case "Backspace":
        case "Delete":
          if (value.length > 0) {
            if (activeIndex !== -1 && activeIndex < value.length) {
              onValueChangeHandler(value[activeIndex]);
              moveCurrent();
            } else {
              if (target.selectionStart === 0) {
                if (selectedValue === inputValue || isValueSelected) {
                  onValueChangeHandler(value[value.length - 1]);
                }
              }
            }
          }
          break;

        case "Enter":
          setOpen(true);
          break;

        case "Escape":
          if (activeIndex !== -1) {
            setActiveIndex(-1);
          } else if (open) {
            setOpen(false);
          }
          break;
      }
    },
    [value, inputValue, activeIndex, loop, dir, onValueChangeHandler]
  );

  return (
    <MultiSelectContext.Provider
      value={{
        value,
        onValueChange: onValueChangeHandler,
        open,
        setOpen,
        inputValue,
        setInputValue,
        activeIndex,
        setActiveIndex,
        ref: inputRef,
        handleSelect,
        keepInputOnSelect,
        tableView,
        onItemClick,
      }}
    >
      <Command
        onKeyDown={handleKeyDown}
        className={cn(
          "overflow-visible bg-transparent flex flex-col",
          className
        )}
        dir={dir}
        {...props}
      >
        {children}
      </Command>
    </MultiSelectContext.Provider>
  );
};

const MultiSelectorTrigger = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { value, onValueChange, activeIndex, tableView, onItemClick } = useMultiSelect();

  const mousePreventDefault = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        "flex flex-col gap-1 p-1 rounded rounded-md border border-input",
        {
          "focus-within:ring-ring": activeIndex === -1,
        },
        className
      )}
      {...props}
    >
      {tableView ? (
        <div className="w-full space-y-1 mb-1">
          {value.map((item, index) => (
            <div
              key={item.id}
              className={cn(
                "flex items-center justify-between w-full p-2 rounded bg-muted/20",
                activeIndex === index && "ring-2 ring-muted-foreground"
              )}
            >
              <div
                className={cn(
                  "flex items-center gap-2",
                  onItemClick && "cursor-pointer hover:text-primary"
                )}
                onClick={() => onItemClick?.(item)}
              >
                {item.image && (
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-8 h-8 object-cover rounded"
                  />
                )}
                <span>{item.name}</span>
              </div>
              <button
                aria-label={`Remove ${item.name} option`}
                aria-roledescription="button to remove option"
                type="button"
                onMouseDown={mousePreventDefault}
                onClick={() => onValueChange(item)}
                className="hover:bg-destructive/10 p-1 rounded"
              >
                <span className="sr-only">Remove {item.name} option</span>
                <RemoveIcon className="h-4 w-4 hover:stroke-destructive" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1">
          {value.map((item, index) => (
            <Badge
              key={item.id}
              className={cn(
                "px-1 rounded- flex items-center text-xs font-normal",
                activeIndex === index && "ring-2 ring-muted-foreground "
              )}
              variant={"lightBlue"}
            >
              <span className="text-xs">{item.name}</span>
              <button
                aria-label={`Remove ${item.name} option`}
                aria-roledescription="button to remove option"
                type="button"
                onMouseDown={mousePreventDefault}
                onClick={() => onValueChange(item)}
              >
                <span className="sr-only">Remove {item.name} option</span>
                <RemoveIcon className="h-4 w-4 hover:stroke-destructive " />
              </button>
            </Badge>
          ))}
        </div>
      )}
      {children}
    </div>
  );
});

MultiSelectorTrigger.displayName = "MultiSelectorTrigger";

const MultiSelectorInput = forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => {
  const {
    setOpen,
    inputValue,
    setInputValue,
    activeIndex,
    setActiveIndex,
    handleSelect,
    ref: inputRef,
  } = useMultiSelect();

  return (
    <div className="flex items-center gap-2 px-2">
      <Search className="h-4 w-4 text-muted-foreground shrink-0" />
      <CommandPrimitive.Input
        {...props}
        tabIndex={0}
        ref={inputRef}
        value={inputValue}
        onValueChange={activeIndex === -1 ? setInputValue : undefined}
        onSelect={handleSelect}
        onBlur={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onClick={() => setActiveIndex(-1)}
        className={cn(
          "bg-transparent outline-none placeholder:text-muted-foreground placeholder:text-xs flex-1",
          className,
          activeIndex !== -1 && "caret-transparent"
        )}
      />
    </div>
  );
});

MultiSelectorInput.displayName = "MultiSelectorInput";

const MultiSelectorContent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children }, ref) => {
  const { open } = useMultiSelect();
  return (
    <div ref={ref} className="relative">
      {open && children}
    </div>
  );
});

MultiSelectorContent.displayName = "MultiSelectorContent";

const MultiSelectorList = forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, children }, ref) => {
  return (
    <CommandList
      ref={ref}
      className={cn(
        "p-2 flex flex-col gap-2 rounded-md scrollbar-thin scrollbar-track-transparent transition-colors scrollbar-thumb-muted-foreground dark:scrollbar-thumb-muted scrollbar-thumb-rounded-lg w-full absolute bg-background shadow-md z-10 border-2 border-grey top-0",
        className
      )}
    >
      {children}
      <CommandEmpty>
        <span className="text-muted-foreground">No results found</span>
      </CommandEmpty>
    </CommandList>
  );
});

MultiSelectorList.displayName = "MultiSelectorList";

const MultiSelectorItem = forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  { value: MultiSelectorItemValue } & Omit<
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>,
    "value" | "onSelect"
  >
>(({ className, value, children, ...props }, ref) => {
  const {
    value: Options,
    onValueChange,
    setInputValue,
    keepInputOnSelect,
  } = useMultiSelect();

  const mousePreventDefault = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const isIncluded = Options.some((item) => item.id === value.id);
  return (
    <CommandItem
      ref={ref}
      {...props}
      value={value.name}
      onSelect={() => {
        onValueChange(value);
        if (!keepInputOnSelect) {
          setInputValue("");
        }
      }}
      className={cn(
        "rounded-sm cursor-pointer px-2 py-1 transition-colors flex justify-between",
        className,
        isIncluded && "opacity-50 cursor-default",
        props.disabled && "opacity-50 cursor-not-allowed"
      )}
      onMouseDown={mousePreventDefault}
    >
      {children}
      {isIncluded && <Check className="h-4 w-4" />}
    </CommandItem>
  );
});

MultiSelectorItem.displayName = "MultiSelectorItem";

export {
  MultiSelector,
  MultiSelectorTrigger,
  MultiSelectorInput,
  MultiSelectorContent,
  MultiSelectorList,
  MultiSelectorItem,
};
